import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useUserCanEditProvider } from "../../../domain/Provider/useIsOrphanedProvider"
import { useSelector } from "react-redux"
import axios from "axios"

import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import {
  dataStatusesSending,
  downloadFileBlob,
  utcDateTimeToLocalDateString,
} from "../../shared/utils"
import { useAppDispatch } from "../../../reduxStore/hooks"
import { selectProvider } from "../../../domain/Provider/model"
import { BASE_URL, getBaseEndpoint } from "../../endpoints/endpointManager"
import { Provider, ProviderDisclosure } from "../../../domain/Provider/schema"
import {
  fetchBaseProvider,
  selectProviderDataStatus,
} from "../../../domain/Provider/loader"
import { DataStatus } from "../../../domain/enums"
import { bannersReset, errorHandlerUpdated } from "../../errors/errorSlice"
import { fetchB64DocxFile, fileDetails } from "../../../domain/CMS/fileService"
import { FileDownload } from "../../shared/components/FileDownload"
import { apiService } from "../../shared/apiService"
import { useFetchDocumentBlob } from "../../../domain/Files/useFetchDocumentBlob"
import { FileStatusDisplayIdEnum } from "../../../domain/Files/schema"
import {
  satisfiesPolicy,
  isPendingProvider,
  isPendingActivationProvider,
  isAffiliateRelations,
  isActiveProvider,
  isUserProvider,
} from "../../authz/authz"
import RemoveModal from "../../shared/components/RemoveModal"
import { useNavigateWithQueryString } from "../../../App"
import {
  putCompletedOnboardingStepsAndUpdateUser,
  UserOnboardingStepsEnum,
} from "../../../domain/Account/userSlice"
import { selectUser } from "../../shared/selectors"
import { SaveAndContinueButton } from "../../shared/components/SaveAndContinue"
import {
  appRoutes,
  getRouteWithParams,
  getRouteWithQueryParams,
} from "../../../routes"
import { selectIsUserOnboarded } from "../../../domain/Practices/selectors"
import { FormattedProfileCard } from "../../layout/FormattedProfileCard"
import useDocusignRedirect, {
  DOCUSIGN_REDIRECT_QUERY_PARAM_KEY,
  DocusignRedirectOrigin,
} from "../../shared/hooks/useDocusignRedirect"
import { DocusignPoller } from "../../shared/components/DocusignPoller"
import { getCurrentFormattedDateForESign } from "../../../domain/Practice/helpers"
import { ErrorMessage } from "../../shared/components/ErrorMessage"
import { ProfilePage } from "../../shared/components/ProfilePage"
import { DocusignLink } from "../../shared/components/DocusignLink"
import { FileUpload } from "../../shared/components/FileUpload/FileUpload"
import { DisclosureTable, DisclosureTableRow } from "./DisclosureTable"
import { usePollData } from "../../shared/hooks/usePollData"
import appInsightsLogger from "../../logging/appInsightsLogger"
import { UnsavedWarning } from "../../practiceProfile/components/UnsavedWarning"
import { WarningModal } from "../../practiceProfile/components/WarningModal"
import { ButtonWrapper } from "../../shared/components/ButtonWrapper"
import { Typography, typographyTypes } from "../../shared/components/Typography"
import FormContainer from "../../shared/components/FormContainer"
import {
  type HeaderType,
  SemanticHeader,
} from "../../shared/components/SemanticHeader"
import Spinner from "../../shared/components/Spinner"
import { MIN_WIDTH_NAV_CHILDREN } from "../../layout/commonStyle"
import { ProviderTypeDisplayId } from "../../../domain/enums/displayId.enum"

//////////////////////////
//// HELPER FUNCTIONS ////
//////////////////////////

// DocuSign Disclosure
const getDisclosureSigningRequestEndpoint = (providerId: string): string => {
  return (
    getBaseEndpoint() +
    `/provider/providers/${providerId}/disclosure/signingrequest`
  )
}

// Disclosure Upload
type DisclosureUpload =
  | {
      fileMetadata: FileType
      fileData: File
    }
  | undefined

// Used for the file upload component
interface Notifications {
  formError: string
  dropzoneError: string
  uploadError: string
  uploadSuccess: string
}

const initialNotifications: Notifications = {
  formError: "",
  dropzoneError: "",
  uploadError: "",
  uploadSuccess: "",
}

const isActiveSignedDisclosure = (disclosure: ProviderDisclosure) => {
  return (
    !disclosure.isSupportingDocument &&
    disclosure.fileId &&
    !disclosure.archivedDateTime &&
    disclosure.fileStatusDisplayId === FileStatusDisplayIdEnum.AVAILABLE
  )
}

const isActiveSupportingDocument = (disclosure: ProviderDisclosure) => {
  return (
    disclosure.isSupportingDocument &&
    disclosure.fileId &&
    !disclosure.archivedDateTime &&
    disclosure.fileStatusDisplayId === FileStatusDisplayIdEnum.AVAILABLE
  )
}

export const hasActiveSignedDisclosure = (provider: Provider) => {
  return (
    provider.providerDisclosures.some(isActiveSignedDisclosure) ||
    provider.providerTypeDisplayId === ProviderTypeDisplayId.MANAGED // Managed providers don't have or need signed disclosures
  )
}

////////////////////////
//// MAIN COMPONENT ////
////////////////////////
const DisclosureV2: React.FC = () => {
  // Redux and Top Level State
  const navigate = useNavigateWithQueryString()
  const dispatch = useAppDispatch()

  const [fileProcessing, setFileProcessing] = useState(false)
  const [fileDeleteInProgress, setFileDeleteInProgress] = useState(false)
  const [notifications, setNotifications] =
    useState<Notifications>(initialNotifications)
  const [pollCount, setPollCount] = useState(0)
  const [docusignErrorOverride, setDocusignErrorOverride] = useState(false)
  const providerDataStatus = useSelector(selectProviderDataStatus)
  const [componentLoading, setComponentLoading] = useState(false)
  const [downloadStatus, setDownloadStatus] = useState(DataStatus.LOADING)
  const downloadSending = dataStatusesSending([downloadStatus])

  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false)
  const [disclosureResetPending, setDisclosureResetPending] = useState(false)

  // CMS
  const {
    cms: cmsContent,
    isLoading: isCMSLoading,
    isReady: isCMSReady,
    hasError: hasCMSError,
  } = useCMSReady(["provider", "common"])
  const commonText = cmsContent.common
  const providerText = cmsContent.provider

  // Provider
  const provider = useSelector(selectProvider)

  // Onboarding
  const isUserOnboarded = useSelector(selectIsUserOnboarded)

  // User
  const user = useSelector(selectUser)
  const canSign = useSelector(
    satisfiesPolicy([
      [isUserProvider, isPendingProvider],
      [isUserProvider, isActiveProvider],
    ]),
  )
  const canUploadOrDeleteSupportingDocumentsExpanded = useSelector(
    satisfiesPolicy([
      [isUserProvider, isPendingProvider],
      [isUserProvider, isActiveProvider],

      [isAffiliateRelations, isPendingProvider],
      [isAffiliateRelations, isPendingActivationProvider],
      [isAffiliateRelations, isActiveProvider],
    ]),
  )

  const providerHasResetDisclosurePermissions = useSelector(
    satisfiesPolicy([[isUserProvider, isPendingProvider]]),
  )

  const affiliateRelationsHasResetDisclosurePermissions = useSelector(
    satisfiesPolicy([[isAffiliateRelations, isActiveProvider]]),
  )

  // Check if provider has progressed past disclosure during onboarding
  const hasProgressedPastDisclosure = useMemo(() => {
    if (isUserOnboarded) return true

    return user.completedUserOnboardingSteps?.includes(
      UserOnboardingStepsEnum.PROVIDER_SETUP,
    )
  }, [isUserOnboarded, user])

  const canProviderResetDisclosure =
    providerHasResetDisclosurePermissions && !hasProgressedPastDisclosure

  const canResetDisclosure =
    canProviderResetDisclosure ||
    affiliateRelationsHasResetDisclosurePermissions

  const hasEditAuth = useUserCanEditProvider(
    provider.providerStatusDisplayId,
    provider.id,
  )

  const pollProviderOnDocusignRedirectComplete = useMemo(() => {
    // Find any non-supporting disclosure with a fileId that is NOT in PROCESSING
    const hasCompletedDisclosure = provider.providerDisclosures.some(
      (disclosure) =>
        !!disclosure.fileId &&
        !disclosure.isSupportingDocument &&
        disclosure.fileStatusDisplayId !== FileStatusDisplayIdEnum.PROCESSING,
    )
    return hasCompletedDisclosure || docusignErrorOverride
  }, [docusignErrorOverride, provider.providerDisclosures])

  const { docusignRedirectPollingRequired } = useDocusignRedirect({
    [DocusignRedirectOrigin.DISCLOSURE]: pollProviderOnDocusignRedirectComplete,
  })

  const MAX_POLL_ATTEMPTS = 10
  const pollLimitReached = useMemo(() => {
    return (
      pollCount >= MAX_POLL_ATTEMPTS &&
      providerDataStatus.baseProviderDataStatus !== DataStatus.SENDING
    )
  }, [pollCount, providerDataStatus.baseProviderDataStatus])

  const docusignErrorArgs = useMemo(() => {
    return !pollProviderOnDocusignRedirectComplete && pollLimitReached
      ? {
          message:
            "Disclosure document failed to fetch after docusign redirect.",
          onClick: () => setDocusignErrorOverride(true),
        }
      : undefined
  }, [pollLimitReached, pollProviderOnDocusignRedirectComplete])

  // Header
  const SECTION_HEADER_TYPE = "sectionHeader" as HeaderType

  // DocuSign Disclosure
  const [signedDocument, setSignedDocument] = useState<ProviderDisclosure>()

  const downloadProviderFile = useCallback(
    async (providerId: string, fileId: string) => {
      dispatch(bannersReset())
      const response = await apiService.provider.get.PROVIDER_DISCLOSURE_DOC({
        providerId,
        fileId,
      })
      if (
        response.status === FileStatusDisplayIdEnum.AVAILABLE &&
        response.data
      ) {
        const blob: Blob = response.data
        return blob
      } else if (response.status === FileStatusDisplayIdEnum.PROCESSING) {
        dispatch(
          errorHandlerUpdated({
            infoBanner: {
              isVisible: true,
              title: commonText?.warningBannerTitle || "",
              content: commonText?.fileProcessing || "",
            },
          }),
        )
      } else {
        dispatch(
          errorHandlerUpdated({
            errorBanner: {
              isVisible: true,
              title: commonText?.errorBannerTitle || "",
              content: commonText?.fileDownloadError || "",
            },
          }),
        )
        console.error(
          `Failed to get provider file with error: ${response.status}`,
        )
      }
    },
    [commonText, dispatch],
  )

  const downloadSignedDocument = useFetchDocumentBlob({
    fileApiCall: () =>
      apiService.provider.get.PROVIDER_DISCLOSURE_DOC({
        providerId: provider.id ?? "",
        fileId: signedDocument?.fileId ?? "",
      }),
    setDownloadStatus,
  })

  useEffect(() => {
    if (provider) {
      const activeDisclosure = provider.providerDisclosures.find(
        isActiveSignedDisclosure,
      )

      // Update the local signedDocument when any of these meaningful
      // changes happen on the provider side:
      // - no signedDocument yet
      // - fileId changed (new upload)
      // - file status changed (e.g. PROCESSING -> AVAILABLE)
      // - updatedDateTime changed (server-side timestamp)
      if (activeDisclosure) {
        const activeUpdatedTs = activeDisclosure.updatedDateTime
          ? Date.parse(activeDisclosure.updatedDateTime)
          : undefined
        const signedUpdatedTs = signedDocument?.updatedDateTime
          ? Date.parse(signedDocument.updatedDateTime)
          : undefined

        const shouldSetSignedDocument =
          !signedDocument ||
          activeDisclosure.fileId !== signedDocument.fileId ||
          activeDisclosure.fileStatusDisplayId !==
            signedDocument.fileStatusDisplayId ||
          activeUpdatedTs !== signedUpdatedTs
        if (shouldSetSignedDocument) {
          setSignedDocument(activeDisclosure)
        }
      }
    }
  }, [provider, signedDocument])

  const handleOnDocusignClick = async () => {
    if (canSign) {
      try {
        setComponentLoading(true)
        const url = getDisclosureSigningRequestEndpoint(provider.id ?? "")

        const documentBase64 = await fetchB64DocxFile(
          fileDetails.disclosures.staticUrl,
        )

        const redirectUrl = getRouteWithQueryParams(
          BASE_URL.concat(appRoutes.provider.DISCLOSURE_ROUTE),
          {
            providerId: provider.id || "",
            [DOCUSIGN_REDIRECT_QUERY_PARAM_KEY]:
              DocusignRedirectOrigin.DISCLOSURE,
          },
        )
        const data = {
          currentDate: getCurrentFormattedDateForESign(),
          documentBase64: documentBase64,
          documentName: "Disclosure Form",
          documentType: "DOCX",
          documentId: crypto.randomUUID(),
          signerName: `${provider.firstName} ${provider.lastName}`,
          signerEmail:
            provider.providerEmails.find(
              (email) => email.emailTypeDisplayId == "primary",
            )?.address ?? "no@primary.com",
          signerClientId: user.id,
          redirectUrl,
        }
        const response = await axios.post(url, data)
        if (response.status === 200) {
          const signingRequestUrl = response.data.embeddedSigningURL
          window.location.href = signingRequestUrl
        }
      } catch (error) {
        console.error(error)
        setHasDocusignError(true)
        setComponentLoading(false)
      }
    }
  }

  // Disclosure Upload
  const [disclosureUpload, setDisclosureUpload] =
    useState<DisclosureUpload>(undefined)
  const [uploadedSupports, setUploadedSupports] = useState<
    ProviderDisclosure[]
  >([])

  useEffect(() => {
    if (provider) {
      const disclosures = provider.providerDisclosures

      const supportingDocuments = disclosures.filter(isActiveSupportingDocument)

      setUploadedSupports(supportingDocuments)
    }
  }, [provider, disclosureUpload])

  const onFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    errorMessage?: string,
  ) => {
    // Clear old Error Banner if possible
    dispatch(
      errorHandlerUpdated({
        errorBanner: {
          isVisible: false,
          title: "",
          content: "",
        },
      }),
    )

    if (event.target.files) {
      const newFile = event.target.files[0]
      const newFileWithId: FileType = {
        id: "placeholder",
        name: newFile.name,
        uploadStatus: "uploading",
      }

      setDisclosureUpload({
        fileMetadata: newFileWithId,
        fileData: newFile,
      })
      setNotifications((prevState) => ({
        ...prevState,
        dropzoneError: "",
        uploadError: errorMessage || "",
        uploadSuccess: "",
        formError: "",
      }))
    }
  }

  const onRemoveClick = () => {
    setDisclosureUpload(undefined)
  }

  const onUploadClick = async () => {
    setFileProcessing(true)
    if (
      disclosureUpload?.fileData &&
      disclosureUpload?.fileMetadata.name &&
      disclosureUpload?.fileMetadata.id
    ) {
      const updatedProvider = {
        ...provider,
        providerDisclosures: [
          ...provider.providerDisclosures,
          {
            externalId: disclosureUpload.fileMetadata.id,
            isSupportingDocument: true,
            providerId: provider.id,
          } as ProviderDisclosure,
        ],
      }
      try {
        const providerResponse = await axios.put<Provider>(
          getBaseEndpoint() + `/provider/providers/${provider.id}`,
          updatedProvider,
        )

        if (
          (providerResponse && providerResponse.status === 200) ||
          providerResponse.status === 201
        ) {
          const updatedProviderData = providerResponse.data
          const addedDisclosures =
            updatedProviderData.providerDisclosures.filter(
              (providerDisclosure) =>
                !updatedProvider.providerDisclosures.some(
                  (updatedDisclosure) =>
                    providerDisclosure.id === updatedDisclosure.id,
                ),
            )

          const entityId = addedDisclosures[0].id ?? ""
          const url =
            getBaseEndpoint() + `/provider/providers/${provider.id}/files`
          const formData = new FormData()
          formData.append(
            "FileData",
            disclosureUpload.fileData,
            disclosureUpload.fileMetadata.name,
          )
          formData.append("FileName", disclosureUpload.fileMetadata.name)
          formData.append("EntityType", "disclosure")
          formData.append("EntityId", entityId)

          setDisclosureUpload((prevState) => ({
            ...prevState,
            fileData: disclosureUpload.fileData,
            fileMetadata: {
              ...disclosureUpload.fileMetadata,
              id: entityId,
              uploadStatus: "uploading",
            },
          }))

          const response = await axios.post(url, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })

          if (response.status === 200) {
            await dispatch(
              fetchBaseProvider({
                providerId: provider.id ?? "",
              }),
            )
          } else {
            setDisclosureUpload((prevState) => ({
              ...prevState,
              fileData: disclosureUpload.fileData,
              fileMetadata: {
                ...disclosureUpload.fileMetadata,
                uploadStatus: "error",
              },
            }))
            setFileProcessing(false)
            setNotifications((prevState) => ({
              ...prevState,
              uploadError: commonText?.fileUploadError || "",
              uploadSuccess: "",
            }))
          }
        }
      } catch (error) {
        appInsightsLogger.traceError("DisclosureUploadError", error)
        setNotifications((prevState) => ({
          ...prevState,
          uploadError: commonText?.uploadFailed || "",
          uploadSuccess: "",
        }))
        setFileProcessing(false)
      }
    }
  }

  const stopPollingCB = useCallback(
    (pollingResponse: Provider[]) => {
      const provider = pollingResponse[0]
      const updatedDisclosure = provider.providerDisclosures.find(
        (disclosure) => disclosure.id === disclosureUpload?.fileMetadata.id,
      )
      if (
        !!updatedDisclosure &&
        updatedDisclosure.fileStatusDisplayId ===
          FileStatusDisplayIdEnum.PROCESSING
      ) {
        return false
      }
      if (
        !!updatedDisclosure &&
        updatedDisclosure.fileStatusDisplayId ===
          FileStatusDisplayIdEnum.AVAILABLE &&
        !!updatedDisclosure.fileId &&
        !!updatedDisclosure.fileName
      ) {
        setDisclosureUpload(undefined)
        setNotifications((prevState) => ({
          ...prevState,
          uploadSuccess: commonText?.fileUploadSuccess || "",
          uploadError: "",
        }))
        dispatch(
          fetchBaseProvider({
            providerId: provider.id!,
          }),
        )
      }
      if (
        !!updatedDisclosure &&
        updatedDisclosure.fileStatusDisplayId ===
          FileStatusDisplayIdEnum.QUARANTINED
      ) {
        setDisclosureUpload((prevState) =>
          !!prevState
            ? {
                ...prevState,
                fileMetadata: {
                  ...prevState.fileMetadata,
                  uploadStatus: "error",
                },
              }
            : undefined,
        )
        setNotifications((prevState) => ({
          ...prevState,
          uploadError: commonText?.malwareUploadError || "",
          uploadSuccess: "",
        }))
      }
      setFileProcessing(false)
      return true
    },
    [
      commonText?.fileUploadSuccess,
      commonText?.malwareUploadError,
      disclosureUpload?.fileMetadata.id,
      dispatch,
    ],
  )
  const handleErrorCb = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (err: unknown) => {
      setNotifications((prevState) => ({
        ...prevState,
        uploadError: commonText?.uploadFailed ?? "",
        uploadSuccess: "",
      }))
      setFileProcessing(false)
    },
    [commonText?.uploadFailed],
  )
  const handlePollingLimitReachedCb = useCallback(() => {
    setNotifications((prevState) => ({
      ...prevState,
      uploadError: commonText?.fileProcessingFailed ?? "",
      uploadSuccess: "",
    }))
    setFileProcessing(false)
  }, [commonText?.fileProcessingFailed])

  const { startPolling } = usePollData(
    stopPollingCB,
    handleErrorCb,
    handlePollingLimitReachedCb,
  )
  useEffect(() => {
    const updatedDisclosure = provider.providerDisclosures.find(
      (disclosure) => disclosure.id === disclosureUpload?.fileMetadata.id,
    )
    if (
      !!updatedDisclosure &&
      updatedDisclosure.fileStatusDisplayId ===
        FileStatusDisplayIdEnum.PROCESSING &&
      !!provider.id
    ) {
      startPolling([
        {
          apiServiceMethod: () =>
            apiService.provider.get.PROVIDER_BY_ID({
              providerId: provider.id!,
            }),
        },
      ])
    }
  }, [
    disclosureUpload?.fileMetadata.id,
    provider.id,
    provider.providerDisclosures,
    startPolling,
  ])

  // Disclosures Table
  const [modalOpen, setModalOpen] = useState(false)
  const [modalFileId, setModalFileId] = useState("")
  const [modalFileName, setModalFileName] = useState("")

  const disclosureTableRows: DisclosureTableRow[] = uploadedSupports.map(
    (support) => {
      return {
        downloadLink: {
          value: support.fileName ?? "",
          node: (
            <ButtonWrapper
              onClick={async () => {
                const blob = await downloadProviderFile(
                  provider.id ?? "",
                  support.fileId ?? "",
                )
                if (!!blob) {
                  downloadFileBlob(
                    blob,
                    support.fileName ?? `disclosure_${Date.now()}.pdf`,
                  )
                }
              }}
              justification="start"
              buttonWrapperStyleType="table"
              styleOverrides="font-[Inter] bg-transparent"
            >
              <Typography
                variant={typographyTypes.HREF}
                text={support.fileName ?? ""}
                styleOverrides="font-sky-600 font-medium no-underline"
              />
            </ButtonWrapper>
          ),
        },
        uploadedBy: {
          value: support.createdByFullName ?? "",
          node: support.createdByFullName ?? "",
        },
        uploadDate: {
          value: !!support.updatedDateTime
            ? new Date(support.updatedDateTime).toISOString()
            : "",
          node: !!support.updatedDateTime
            ? utcDateTimeToLocalDateString(support.updatedDateTime)
            : "",
        },
        onRemoveClick:
          canUploadOrDeleteSupportingDocumentsExpanded && !fileProcessing
            ? () => {
                setModalFileName(support.fileName ?? "")
                setModalFileId(support.fileId ?? "")
                setModalOpen(true)
              }
            : undefined,
      }
    },
  )

  const onRemoveDisclosureClick = useCallback(
    async (fileId: string) => {
      setFileDeleteInProgress(true)
      const updatedDisclosures = provider.providerDisclosures.filter(
        (disclosure) => disclosure.fileId !== fileId,
      )
      const updatedProvider = {
        ...provider,
        providerDisclosures: updatedDisclosures,
      }

      const providerResponse = await axios.put<Provider>(
        getBaseEndpoint() + `/provider/providers/${provider.id}`,
        updatedProvider,
      )

      if (providerResponse.status === 200) {
        setDisclosureUpload(undefined)
        await dispatch(
          fetchBaseProvider({
            providerId: provider.id ?? "",
          }),
        )
      } else {
        console.error("file deletion failed")
      }

      setFileDeleteInProgress(false)
    },
    [dispatch, provider],
  )
  const handleRemoveAndResignClick = () => {
    setShowResetConfirmModal(true)
  }

  const handleResetDisclosureConfirm = async () => {
    try {
      setDisclosureResetPending(true)

      // Call new API endpoint to reset disclosure
      await apiService.provider.put.RESET_PROVIDER_DISCLOSURE(provider.id ?? "")

      // Refetch provider to get updated disclosure status
      await dispatch(fetchBaseProvider({ providerId: provider.id ?? "" }))
      setSignedDocument(undefined)
    } catch (error) {
      appInsightsLogger.traceError("Error resetting disclosure:", error)
      dispatch(
        errorHandlerUpdated({
          errorBanner: {
            isVisible: true,
            title:
              cmsContent?.common?.errorBannerInternalServerErrorTitle || "",
            content:
              cmsContent?.common?.errorBannerInternalServerErrorContent || "",
          },
        }),
      )
    } finally {
      setDisclosureResetPending(false)
      setShowResetConfirmModal(false)
    }
  }

  useEffect(() => {
    if (!pollLimitReached && docusignRedirectPollingRequired) {
      const refreshAgreements = setInterval(async () => {
        dispatch(fetchBaseProvider({ providerId: provider.id ?? "" }))
        setPollCount((prevCount) => prevCount + 1)
      }, 3000)
      return () => clearInterval(refreshAgreements)
    }
  }, [dispatch, pollLimitReached, provider.id, docusignRedirectPollingRequired])

  const header = {
    text: providerText?.disclosureHeader || "",
    type: "pageHeader" as HeaderType,
    id: "provider-disclosure-header",
  }

  const [hasDocusignError, setHasDocusignError] = useState(false)
  if (hasDocusignError) {
    return (
      <ErrorMessage
        type="general"
        clickHandlerOverride={() => {
          // Get the current URL and navigate to it for a full page reload
          window.location.href = window.location.href
        }}
        textOverrides={{
          header: cmsContent.common?.docusignErrorModalHeader,
          content: cmsContent.common?.docusignErrorModalText,
          buttonText: cmsContent.common?.docusignErrorModalButton,
        }}
      />
    )
  }
  if (isCMSLoading) {
    return <Spinner />
  } else if (hasCMSError || !isCMSReady) {
    const errorMessage = `DisclosureV2 -- Error loading provider disclosure content from CMS`
    appInsightsLogger.traceError(errorMessage)
    throw new Error(errorMessage)
  } else if (componentLoading || disclosureResetPending) {
    return (
      <FormattedProfileCard styleOverrides="m-0">
        <Spinner
          text={componentLoading ? commonText!.rerouteToDocusign : undefined}
        />
      </FormattedProfileCard>
    )
  } else {
    const signedByUserName = provider.firstName + " " + provider.lastName
    const timestamp = signedDocument?.updatedDateTime ?? Date.now()

    const disclosureSignedByStampText = `${
      commonText!.signedBy
    } ${signedByUserName} ${commonText!.on} ${
      timestamp ? utcDateTimeToLocalDateString(timestamp.toString()) : ""
    }`

    const disclosureDownloadText = {
      linkText: providerText!.disclosureDownload,
      submittedText: disclosureSignedByStampText,
      errorText: commonText!.fileDownloadError,
      removeText: canResetDisclosure
        ? providerText!.disclosureRemove
        : undefined,
    }

    const isSaveAndCompleteDisabled =
      !signedDocument ||
      signedDocument.fileStatusDisplayId !== FileStatusDisplayIdEnum.AVAILABLE

    const currentSelectedFile = disclosureUpload
      ? {
          name: disclosureUpload.fileMetadata.name,
          isUploading: fileProcessing,
        }
      : undefined

    const showTableComponent = disclosureTableRows.length > 0
    const fileDropzoneIsOpenByDefault = !isUserOnboarded || !showTableComponent

    const disclosureChildComponent = docusignRedirectPollingRequired ? (
      <DocusignPoller containerStyleOverrides="m-0" error={docusignErrorArgs} />
    ) : (
      <ProfilePage styleOverrides="px-27 py-12">
        <FormContainer header={header}>
          <SemanticHeader
            text={providerText!.disclosureSubHeader}
            type={SECTION_HEADER_TYPE}
            styleOverrides="m-0"
            headerLevel="h4"
          />
          {!signedDocument && (
            <div className="!mt-0">
              <DocusignLink
                onClick={handleOnDocusignClick}
                readOnly={!canSign}
                text={providerText!.disclosureDocusign}
              />
            </div>
          )}
          {signedDocument && (
            <>
              <FileDownload
                text={disclosureDownloadText}
                onClick={async () => {
                  const blob = await downloadSignedDocument()
                  if (!!blob) {
                    downloadFileBlob(
                      blob,
                      `Disclosure_${provider.firstName} ${provider.lastName}`,
                    )
                  }
                }}
                onRemove={handleRemoveAndResignClick}
                loading={downloadSending}
              />
              <FileUpload
                title={providerText!.disclosureOptionalDocumentation}
                tableTitle={commonText!.uploadedFiles}
                description={
                  <Typography
                    variant={typographyTypes.HELPER}
                    text={providerText!.disclosureOptionalDocumentationDetails}
                    styleOverrides="inline"
                  />
                }
                showAccordion={canUploadOrDeleteSupportingDocumentsExpanded}
                selectedFile={currentSelectedFile}
                defaultOpen={fileDropzoneIsOpenByDefault}
                onFileChange={onFileChange}
                onRemoveClick={onRemoveClick}
                onUploadClick={onUploadClick}
                dropzoneErrorMessage={notifications.dropzoneError}
                fileUploadErrorMessage={notifications.uploadError}
                uploadSuccessMessage={notifications.uploadSuccess}
                tableComponent={
                  showTableComponent ? (
                    <DisclosureTable files={disclosureTableRows} />
                  ) : undefined
                }
              />
            </>
          )}
          {hasEditAuth && (
            <div className="!mt-11 w-fit">
              {!isUserOnboarded && !hasActiveSignedDisclosure(provider) && (
                <div className="mb-4">
                  <Typography
                    variant={typographyTypes.NORMAL}
                    text={providerText!.disclosureRequiredText}
                  />
                </div>
              )}
              {!isUserOnboarded &&
                !(fileProcessing || fileDeleteInProgress) && (
                  <SaveAndContinueButton
                    onSave={async () => {
                      if (!isUserOnboarded) {
                        await dispatch(
                          putCompletedOnboardingStepsAndUpdateUser({
                            userId: user.id,
                            completedOnboardingSteps: [
                              UserOnboardingStepsEnum.PROVIDER_SETUP,
                            ],
                          }),
                        )
                      }
                      const activationRoute: string = getRouteWithParams(
                        appRoutes.ACTIVATION_ROUTE,
                      )
                      navigate(activationRoute, undefined, {
                        replace: !isUserOnboarded,
                      })
                    }}
                    disabled={isSaveAndCompleteDisabled}
                    label={providerText!.saveAndCompleteProviderSetup}
                    ariaLabel={
                      providerText!.saveAndCompleteProviderSetupAriaLabel
                    }
                  />
                )}
            </div>
          )}
        </FormContainer>
      </ProfilePage>
    )

    return (
      <FormattedProfileCard
        styleOverrides="m-0"
        fixedMinWidth={MIN_WIDTH_NAV_CHILDREN.PROVIDER_DISCLOSURE}
      >
        {disclosureChildComponent}
        <RemoveModal
          text={{
            remove: commonText?.archive || "Archive",
            itemToRemove:
              providerText?.disclosureDeleteConfirmation.replace(
                "{modalFileName}",
                modalFileName,
              ) + "?",
            submitButton: commonText?.archive || "Archive",
          }}
          isOpen={modalOpen}
          onCancel={() => {
            setModalOpen(false)
          }}
          onSubmit={async () => {
            await onRemoveDisclosureClick(modalFileId)
            setModalOpen(false)
          }}
          submitLoading={fileDeleteInProgress}
        />
        <WarningModal
          isOpen={showResetConfirmModal}
          text={{
            title: commonText?.warningDialogTitle,
            confirmButton: commonText?.continue,
            cancelButton: commonText?.cancel,
          }}
          onConfirm={handleResetDisclosureConfirm}
          onCancel={() => setShowResetConfirmModal(false)}
        >
          {providerText?.disclosureRemoveModalMessagePartOne}{" "}
          {providerText?.disclosureRemoveModalMessagePartTwo}
        </WarningModal>
        <UnsavedWarning when={disclosureUpload !== undefined} />
      </FormattedProfileCard>
    )
  }
}
export default DisclosureV2
